import { useRef, useState, useEffect } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import ReactCrop, { type Crop, centerCrop, makeAspectCrop } from 'react-image-crop'
import { ZoomIn, ZoomOut } from 'lucide-react'
import 'react-image-crop/dist/ReactCrop.css'

interface ImageCropDialogProps {
  /** Object URL dari gambar yang dipilih. null = dialog tertutup. */
  imageSrc: string | null
  onConfirm: (croppedBlob: Blob) => void
  onCancel: () => void
  /** Aspect ratio untuk crop (w/h). Default: 9/16 (portrait). */
  aspect?: number
}

/**
 * Ekstrak area crop dari gambar sebagai WebP blob.
 * crop dalam unit '%' (hasil percentCrop dari react-image-crop).
 * Output = pixel asli gambar bukan ukuran tampilan.
 */
async function getCroppedBlob(img: HTMLImageElement, crop: Crop, quality = 0.85): Promise<Blob> {
  const scaleX = img.naturalWidth / img.width
  const scaleY = img.naturalHeight / img.height

  const srcX = (crop.x / 100) * img.width * scaleX
  const srcY = (crop.y / 100) * img.height * scaleY
  const srcW = (crop.width / 100) * img.width * scaleX
  const srcH = (crop.height / 100) * img.height * scaleY

  const canvas = document.createElement('canvas')
  canvas.width = Math.round(srcW)
  canvas.height = Math.round(srcH)
  canvas.getContext('2d')!.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, canvas.width, canvas.height)

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => blob ? resolve(blob) : reject(new Error('Crop gagal')),
      'image/webp',
      quality,
    )
  })
}

// Tinggi tetap area crop dalam dialog
const CROP_AREA_VH = 55

export function ImageCropDialog({ imageSrc, onConfirm, onCancel, aspect = 9 / 16 }: ImageCropDialogProps) {
  const imgRef = useRef<HTMLImageElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [crop, setCrop] = useState<Crop>()
  const [zoom, setZoom] = useState(1)
  // Dimensi gambar setelah di-fit ke container (zoom=1)
  const [baseSize, setBaseSize] = useState<{ width: number; height: number } | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)

  // Saat zoom berubah, auto-scroll agar crop area tetap terlihat di tengah
  useEffect(() => {
    if (!containerRef.current || !baseSize || !crop) return
    const el = containerRef.current
    const imgW = baseSize.width * zoom
    const imgH = baseSize.height * zoom
    const cx = ((crop.x + crop.width / 2) / 100) * imgW
    const cy = ((crop.y + crop.height / 2) / 100) * imgH
    el.scrollLeft = cx - el.clientWidth / 2
    el.scrollTop = cy - el.clientHeight / 2
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom])

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const { width, height } = e.currentTarget
    // Simpan ukuran fit (sebelum zoom diterapkan)
    setBaseSize({ width, height })
    setZoom(1)
    const initial = centerCrop(
      makeAspectCrop({ unit: '%', width: 90 }, aspect, width, height),
      width,
      height,
    )
    setCrop(initial)
  }

  async function handleConfirm() {
    if (!imgRef.current || !crop) return
    setIsProcessing(true)
    try {
      const blob = await getCroppedBlob(imgRef.current, crop)
      onConfirm(blob)
    } catch {
      // onConfirm tidak dipanggil jika crop gagal
    } finally {
      setIsProcessing(false)
    }
  }

  // Sebelum onImageLoad: fit ke tinggi container agar tidak overflow
  // Setelah onImageLoad: ukuran absolut × zoom agar scroll bekerja benar
  const imgStyle: React.CSSProperties = baseSize
    ? { width: `${zoom * baseSize.width}px`, height: `${zoom * baseSize.height}px`, maxWidth: 'none', display: 'block' }
    : { maxHeight: `${CROP_AREA_VH}vh`, width: 'auto', display: 'block' }

  return (
    <Dialog.Root open={!!imageSrc} onOpenChange={(open) => { if (!open) onCancel() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-1/2 z-50 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-card p-6 shadow-xl focus:outline-none"
        >
          <Dialog.Title className="mb-1 text-base font-semibold">Sesuaikan Foto</Dialog.Title>
          <p className="mb-4 text-xs text-muted-foreground">
            Seret untuk memilih area · Gunakan slider untuk zoom.
          </p>

          {imageSrc && (
            <>
              {/* Area crop — fixed height, scroll saat zoom > 1 */}
              <div
                ref={containerRef}
                className="overflow-auto rounded-lg bg-black"
                style={{ height: `${CROP_AREA_VH}vh` }}
              >
                {/* Wrapper min-size agar gambar kecil tetap terpusat */}
                <div className="flex min-h-full min-w-full items-center justify-center">
                  <ReactCrop
                    crop={crop}
                    onChange={(_, percentCrop) => setCrop(percentCrop)}
                    aspect={aspect}
                  >
                    <img
                      ref={imgRef}
                      src={imageSrc}
                      alt="Foto yang dipilih"
                      onLoad={onImageLoad}
                      style={imgStyle}
                    />
                  </ReactCrop>
                </div>
              </div>

              {/* Zoom slider */}
              <div className="mt-3 flex items-center gap-3">
                <ZoomOut className="h-4 w-4 shrink-0 text-muted-foreground" />
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.05}
                  value={zoom}
                  onChange={(e) => setZoom(parseFloat(e.target.value))}
                  className="flex-1 accent-primary"
                  aria-label="Zoom"
                />
                <ZoomIn className="h-4 w-4 shrink-0 text-muted-foreground" />
              </div>
            </>
          )}

          <div className="mt-4 flex justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md border border-input px-4 py-2 text-sm font-medium transition-colors hover:bg-accent"
            >
              Batal
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!crop || isProcessing}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
            >
              {isProcessing ? 'Memproses...' : 'Terapkan'}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
